import { LoaderActions } from '@store/loaders'
import { PERSONAL_WORKSPACE_ID } from '@store/reducers/dashboard/constants'
import { LimitReachedActions } from '@store/reducers/limitReached'
import { ErrorNotification } from '@storeOld/reducers/notification'
import { push } from 'redux-first-history'
import { call, put } from 'redux-saga/effects'

import API from '@/api'
import { ERROR_LIMIT_REACHED } from '@/constants/constants'
import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import type { TemplatesActions } from '../actions'
import type { TemplateCopy } from '../types'

// The PHP endpoint answers a used-up plan with a bare-text 422 body, and the
// legacy client rejects with that body as is — so the caught error is the
// string itself, not an Error. Once the endpoint moves to Nest, swap this for
// the parseNestError + PROJECTS_LIMIT_REACHED check the dashboard sagas use.
const isLimitReached = (error: unknown) => error === ERROR_LIMIT_REACHED

export function* useTemplate({
  payload: { id, workspaceId },
}: ReturnType<typeof TemplatesActions.use>) {
  sendGtmEvent(GtmTriggerName.TEMPLATE_USE, 'Use template from a card')

  // The card shows its spinner while the copy is made, like an opening project.
  yield put(LoaderActions.set(id))

  try {
    // The personal workspace sends no teamId: the copy lands there, owned by
    // the user. A team workspace passes its uuid and the copy joins the team.
    const payload =
      workspaceId === PERSONAL_WORKSPACE_ID ? {} : { teamId: workspaceId }

    const project: TemplateCopy = yield call(API.Template.use, id, payload)

    yield put(push(`/${project.id}`))
  } catch (error) {
    if (isLimitReached(error)) {
      yield put(LimitReachedActions.alert())

      return
    }

    console.error('Failed to use template', error)

    yield put(ErrorNotification('Failed to create a project from the template'))
  } finally {
    yield put(LoaderActions.unset(id))
  }
}
